import React, { useCallback, useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';

import Spinner from '~/components/Common/Spinner';

import { useGetAffiliationList } from '~/hooks/apis/agent/useGetAffiliationList';
import { useGetEoi } from '~/hooks/apis/agent/useGetEoi';
import { useUpdateEoiDetails } from '~/hooks/apis/agent/useUpdateEoiDetails';

import { Eoi } from '~/types/services/agentHub';

import { noop } from '~/utils/noop';

import AgencySubmissionDetails from './AgencySubmissionDetails';
import AgencySubmissionEditor from './AgencySubmissionEditor';

interface Props {
  id: string;
  onClose?: () => void;
  onUpdate?: () => void;
}

export default function AgencySubmissionDetailModal({ id, onClose = noop, onUpdate = noop }: Props) {
  const [editing, setEditing] = useState(false);

  const { data: eoiDetails, isLoading: isLoadingEoi } = useGetEoi(id);
  const { data: availableAffiliations = [], isLoading: isLoadingAffiliations } = useGetAffiliationList(
    eoiDetails?.regionCode,
  );
  const updateEoiMutation = useUpdateEoiDetails();

  const isLoading = isLoadingEoi || isLoadingAffiliations;

  const closeModal = useCallback(() => {
    setEditing(false);
    onClose();
  }, [onClose]);

  const onSave = useCallback(
    async (eoiData: Eoi) => {
      updateEoiMutation.mutate(
        { id, eoiData },
        {
          onSuccess: () => {
            setEditing(false);
            onUpdate();
          },
        },
      );
    },
    [id, updateEoiMutation, onUpdate],
  );

  const onCancel = useCallback(() => {
    setEditing(false);
  }, []);

  return (
    <Dialog open onClose={closeModal} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Box flexGrow={1}>Agency Submission Details</Box>
          <Box>
            <IconButton onClick={closeModal}>
              <CloseIcon color="primary" />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        {isLoading && <Spinner />}
        {!isLoading && editing && (
          <AgencySubmissionEditor
            eoiData={eoiDetails}
            availableAffiliations={availableAffiliations}
            onCancel={onCancel}
            onSave={onSave}
            isSaving={updateEoiMutation.isPending}
          />
        )}
        {!isLoading && !editing && eoiDetails && (
          <AgencySubmissionDetails eoiDetails={eoiDetails} setEditing={setEditing} onClose={closeModal} />
        )}
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={closeModal}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
